import { Button, FormControl, Grid, Group, Layout, Page, Tooltip } from "@/comn/components";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { useFetch, useForm, useGrid, usePage, useResource, useStore, useToast, useAuth } from "@/comn/hooks";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    APIS,
    URLS,
    SF_INTF_DOC_HIST_SRCH,
    SG_INTF_DOC_HIST_LIST,
} from "@/tz/go/egaz/osci/ptl/intf/hist/services/OsciDocRcpnMgmtService";

/*
 * Interface Document Send/Reception Management
 * !== 인터페이스 송/수신 관리 ==!
 */
export const OSCI0113001Q = () => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0102" },
            { area: "comnCd", comnCd: "OSC0104" },
            { area: "comnCd", comnCd: "OSC0109" },
            { area: "comnCd", comnCd: "OSC0088" }, // 업무 분류 코드
            { area: "orgAbrt" }, // 업무 분류 코드
            { area: "orgCd" }, // 업무 분류 코드
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        intfDocHistSrch: useForm({
            defaultSchema: SF_INTF_DOC_HIST_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
                startFrstRgsrDtm: pgeStore?.form?.startFrstRgsrDtm || comnUtils.getDate(),
                endFrstRgsrDtm: pgeStore?.form?.endFrstRgsrDtm || comnUtils.getDate(),
            },
        }),
    };

    const grid = {
        intfDocHistLst: useGrid({
            defaultSchema: SG_INTF_DOC_HIST_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get a list of Receive electronic documents. !== 전자문서 수신 목록을 조회한다. ==!
        getIntfDocHistList: useFetch({
            api: (page = grid.intfDocHistLst.page) =>
                APIS.getIntfDocHistList(form.intfDocHistSrch.getValues(), page, grid.intfDocHistLst.size),
            enabled: comnUtils.isEmpty(form.intfDocHistSrch.errors),
            key: [grid.intfDocHistLst.page, grid.intfDocHistLst.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.intfDocHistSrch.getValues(),
                    page: grid.intfDocHistLst.page,
                    size: grid.intfDocHistLst.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        getIntfDocHistList: (page: number = 0) => {
            form.intfDocHistSrch.handleSubmit(
                () => {
                    grid.intfDocHistLst.setPage(page);
                    fetch.getIntfDocHistList.fetch(page);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_intfDocHistLst: {
            cell: {
                trscLogId: (props: any) => {
                    const { rowValues, value } = props;
                    return <Link to={`${URLS.osci0113002s}/${rowValues.trscLogId}`}>{value}</Link>;
                },
                intfId: (props: any) => {
                    const { rowValues, value } = props;
                    return (
                        <Tooltip content={rowValues.intfNm} enabled={!!rowValues.intfNm}>
                            {value}
                        </Tooltip>
                    );
                },
                rcveOrgCd: (props: any) => {
                    const { rowValues, value } = props;
                    console.log("=========>" + rowValues.rcveOrgCd);
                    if (rowValues.rcveOrgCd === "TANCIS") {
                        return (
                            <Layout direction="col" gap={1} valign="center">
                                {`${rowValues.rcveOrgCd}`}
                            </Layout>
                        );
                    }
                },
            },
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {}, []);

    const kwdSel = form.intfDocHistSrch.watch("kwdSel");

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.intfDocHistSrch.schema.intfId}></Group.Control>
                            <Group.Control {...form.intfDocHistSrch.schema.startTimeRnge}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocHistSrch.schema.sendOrgCd}></Group.Control>
                            <Group.Control {...form.intfDocHistSrch.schema.rcveOrgCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocHistSrch.schema.sndrcvDvnCd}></Group.Control>
                            <Group.Control {...form.intfDocHistSrch.schema.trscStatCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Label label={"L_KWD"} />
                            <Group.Col padding={1}>
                                <Layout direction="row" gap={1}>
                                    <FormControl {...form.intfDocHistSrch.schema.kwdSel} />
                                    <FormControl {...form.intfDocHistSrch.schema.kwdTxt} readOnly={!kwdSel} />
                                </Layout>
                            </Group.Col>
                            <Group.Control {...form.intfDocHistSrch.schema.trscLogId}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role={"reset"}
                                onClick={() => {
                                    form.intfDocHistSrch.reset();
                                    form.intfDocHistSrch.setValues({
                                        startFrstRgsrDtm: comnUtils.getDate(),
                                        endFrstRgsrDtm: comnUtils.getDate(),
                                        intfId: "",
                                        sendOrgCd: "",
                                        rvceOrgCd: "",
                                        sndrcvDvnCd: "",
                                        intfSttsCd: "",
                                        kwdSel: "",
                                        kwdTxt: "",
                                    });
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    handler.getIntfDocHistList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.intfDocHistLst.grid}
                            data={fetch.getIntfDocHistList.data?.intfDocHistList}
                            render={render.grid_intfDocHistLst}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
